import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import actions from "../actions";
import {
  Box,
  Text,
  Stack,
  Image,
  Icon,
  HStack,
  VStack,
  useBreakpointValue,
  useToast,
  Link,
  Heading,
  chakra,
} from "@chakra-ui/react";
import { useCollapse } from "@collapsed/react";
import { KYDWhitelabelEvent as IKYDEvent } from "@common/interfaces/KYDEvent";
import {
  KYDWhitelabelTicketType as KYDTicketType,
  KYDWaitlistBuyOrder,
} from "@common/interfaces/KYDTicket";
import { KYDCartV2 } from "@common/interfaces/KYDCart";
import { CardHolder, Wallet } from "@common/interfaces/KYDUser";
import { CognitoUser } from "amazon-cognito-identity-js";
import * as AWSAmplify from "aws-amplify";
import Div100vh from "react-div-100vh";

import KYDEventDetail from "./EventDetail";
import KYDEventWallet from "./EventWallet";
import Cart from "./Cart";
import TipTapRenderer from "./TipTapRenderer";
import {
  useParams,
  useNavigate,
  Routes,
  Route,
  useSearchParams,
} from "react-router-dom";
import logoFooter from "../lightsmall.png";
import logoFooterDark from "../darksmall.png";

import { FaShoppingCart, FaCalendar, FaMapPin } from "react-icons/fa";

import {
  analytics,
  logger,
  AUTH_STATES,
  markdownToLineBreaks,
  ModalType,
  PIXELS,
  showIntercom,
  signinIntercom,
  groupBy,
  getRandomString,
} from "../utils";
import "react-phone-input-2/lib/bootstrap.css";

import KYDSuccess from "./Success";
import KYDCountdown from "./Countdown";

import { AddToCart, InitiateCheckout, Purchase } from "react-facebook-pixel";
import { useDialog, useDialogWithOptions } from "../Common/Dialog";
import { useErrors } from "../hooks/useErrors";

import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import StripeInterface from "@stripe/stripe-js";
import JoinWaitlistModal, {
  JoinWaitlistModalContext,
  withWaitlistModal,
} from "./JoinWaitlistModal";
import WaitlistCheckout from "./WaitList/WaitlistCheckout";
import WaitlistPaymentMethodConfirmationModal, {
  WaitlistPaymentMethodConfirmationModalContext,
  withWaitlistPaymentMethodConfirmationModal,
} from "./WaitlistPaymentMethodConfirmation";
import { usePolling } from "./WaitList/hooks";
import FullScreenModal, {
  FullScreenModalContext,
  withFullScreenModal,
} from "../Common/FullScreenModal";
import { ticketTypeHasWaitlistBuyOrder } from "./WaitList/utils";
import Markdown from "react-markdown";
import EventFooter from "./EventFooter";
import EventImage from "./StoryMode/EventImage";
import Lineup from "./Lineup";
import BodyDocRenderer from "./BodyDocRenderer";

const { Auth } = AWSAmplify;

const InlineText = chakra.span;

export type CheckoutParams = {
  type: "card" | "express" | "free";
  cardHolder?: CardHolder;
  formData?: any;
  payment_token?: any;
};

function KYDEvent({
  authState,
  currentUser,
  modalType,
  setModalType,
  setWallet,
  wallet,
}: {
  authState: AUTH_STATES;
  currentUser: CognitoUser | null;
  modalType: ModalType;
  setModalType: (type: ModalType, content?: string) => void;
  setWallet: (wallet: Wallet | null) => void;
  wallet: Wallet | null;
}) {
  const [kydEvent, setKydEvent] = useState<IKYDEvent | undefined>(undefined);
  const [cart, setCart] = useState<KYDCartV2 | null>(null);
  const [draftCart, setDraftCart] = useState<{
    id?: string;
    ticket_types?: Record<string, number>;
    total?: number;
    interupted?: boolean;
    reminder?: boolean;
  }>({});
  const [waitlistCart, setWaitlistCart] = useState<null | KYDCartV2>(null);
  const [waitlistPaymentSetupUrl, setWaitlistPaymentSetupUrl] = useState("");
  const [waitlistTicketTypes, setWaitlistTicketTypes] = useState<
    KYDTicketType[]
  >([]);
  const [waitlistAction, setWaitlistAction] = useState<"buy" | "sell" | null>(
    null
  );
  const [waitlistCancellationSettings, setWaitlistCancellationSettings] = useState<{
    enabled: boolean;
    daysBeforeEvent: number;
  }>({
    enabled: false,
    daysBeforeEvent: 1,
  });
  const [isSetTimesExpanded, setIsSetTimesExpanded] = useState(false);

  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  const waitlistPaymentSetupDialog = useDialogWithOptions(
    WaitlistPaymentMethodConfirmationModalContext
  );

  const [postLoginAction, setPostLoginAction] = useState<
    "waitlist" | "add_to_cart" | null
  >();

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [expiresMs, setExpiresMs] = useState(0);
  const dialog = useDialog();
  const waitlistDialog = useDialogWithOptions(JoinWaitlistModalContext);
  const fullScreenModal = useDialogWithOptions(FullScreenModalContext);

  const { event_id } = useParams();
  const navigate = useNavigate();
  const [params, setSearchParams] = useSearchParams();

  const code = params.get("code");
  const [accessCode, setAccessCode] = useState(code);

  const stripeRef = useRef<Stripe>();
  const elementsRef = useRef<StripeElements>();
  const radarRef = useRef<any>();

  const [stripeObject, setStripeObject] = useState<Stripe>(null);
  useEffect(() => {
    const fetchStripeObject = async () => {
      // If there is no kydEvent, do not run the loadStripe function.
      if (kydEvent && kydEvent.stripe && kydEvent.stripe.publishable_key) {
        const res = await loadStripe(kydEvent.stripe.publishable_key);
        // When we have got the Stripe object, pass it into our useState.
        setStripeObject(res);
      }
    };
    fetchStripeObject();
  }, [kydEvent]);

  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const linkColor = useBreakpointValue({ base: "#FFF", md: "#4022FC" });

  const descriptionIsExpandable =
    (kydEvent?.description && kydEvent?.description.length > 50) ||
    kydEvent?.description_rich;

  const collapsedHeight = useBreakpointValue({
    base: descriptionIsExpandable ? 60 : 30,
    md: descriptionIsExpandable ? 60 : 30,
  });

  const linkDecoration = useBreakpointValue({ base: "underline", md: "none" });
  const { getCollapseProps, setExpanded, isExpanded } = useCollapse({
    defaultExpanded: false,
    collapsedHeight: collapsedHeight,
  });

  const [_loading, setLoading] = useState<any>({});
  const toast = useToast();

  const updateLoadingState = useCallback(
    (
      key:
        | "auth"
        | "wallet"
        | "cart"
        | "wallet_silent"
        | "reminder"
        | "code"
        | "waitlist"
        | string,
      value: boolean | string
    ) => {
      setLoading((l: any) => {
        return { ...l, [key]: value };
      });
    },
    []
  );

  const presentAuthModal = () => {
    setModalType(ModalType.SIGN_IN_PHONE_NUMBER);
  };

  const fetchCart = useCallback(
    async (
      _event_id: string,
      _type?: "historical"
    ): Promise<KYDCartV2 | null> => {
      try {
        const serverCart = await actions.USER.fetchCart(_event_id, _type);
        return serverCart;
      } catch (err) {}
      return null;
    },
    []
  );

  const clearDraftCart = useCallback(() => {
    setDraftCart({ total: 0, ticket_types: {}, id: "draft" });
  }, []);

  const fetchWallet = useCallback(
    async (_event_id: string) => {
      try {
        setLoading({ wallet: true });
        const serverWallet = await actions.USER.fetchEventWallet(_event_id);

        if (serverWallet) {
          analytics.t("wallet: fetched wallet");
          signinIntercom({
            user_id: serverWallet.user_id,
            email: serverWallet.email,
            first_name: serverWallet.first_name,
            last_name: serverWallet.last_name,
            hmac: serverWallet.intercom_hmac,
          });
          setWallet({ ...serverWallet });
        }
        setLoading({ wallet: false });
        return serverWallet;
      } catch (err) {}
    },
    [setWallet, code]
  );

  const { start: startWalletPolling } = usePolling<any>({
    poll: async () => {
      const wallet = await fetchWallet(kydEvent.id);
      const hasPendingPayment = wallet.buy_waitlist_orders.find(
        (wo) => wo.status === "PENDING_PAYMENT_METHOD"
      );

      return !hasPendingPayment;
    },
  });

  const pollWalletForNewTickets = useCallback(
    async (
      checkout_session_id: string
    ): Promise<"success" | "failed" | "notfound" | "duplicate"> => {
      const sleep = (milliseconds: number) => {
        return new Promise((resolve) => setTimeout(resolve, milliseconds));
      };

      if (!checkout_session_id) {
        return "failed";
      }
      let attempts = 0;
      let didFind = false;

      let ts = Date.now();
      updateLoadingState("wallet_silent", true);

      if (!_loading[checkout_session_id]) {
        updateLoadingState(checkout_session_id, ts.toString());

        do {
          const serverCart = await fetchCart(event_id!);
          if (
            !serverCart ||
            (serverCart &&
              serverCart.last_checkout_session_id === checkout_session_id)
          ) {
            didFind = true;
            await fetchWallet(event_id!);
            break;
          } else if (
            serverCart.stripe &&
            (serverCart.stripe
              .status as StripeInterface.PaymentIntent["status"]) ===
              "requires_payment_method"
          ) {
            return "failed";
          } else {
            await sleep(2000);
            attempts += 1;
          }
        } while (attempts < 15);
      } else {
        return "duplicate";
      }

      updateLoadingState("wallet_silent", false);
      return didFind ? "success" : "notfound";
    },
    [cart, event_id, fetchCart, fetchWallet, updateLoadingState, _loading]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const checkFor3ds = async () => {
      setModalType(ModalType.CHECKING_OUT);
      const checkoutSessionId = queryParams.get("checkout_session_id");
      const server_cart = await fetchCart(event_id, "historical");

      let purchaseData: Purchase = {
        currency: server_cart?.currency || "USD",
        value: (server_cart?.total || 0) / 100,
        num_items: server_cart?.ticket_quantity,
        contents: server_cart?.display_line_items.map((li) => ({
          id: li.entity_type_id,
          quantity: li.display_quantity,
        })),
      };
      PIXELS.purchase(
        purchaseData,
        checkoutSessionId,
        kydEvent?.pixels && kydEvent.pixels.twitter
          ? kydEvent.pixels.twitter.event_purchase_id
          : null,
        null,
        server_cart?.id
      );

      analytics.t("cart: checkout redirect return", {
        value: (cart?.total || 0) / 100,
        mode: "3ds-redirect",
        purchaseData,
      });

      const didFind = await pollWalletForNewTickets(checkoutSessionId);
      if (didFind === "success") {
        analytics.t("cart: checkout success", {
          value: (cart?.total || 0) / 100,
          mode: "3ds-redirect",
          purchaseData,
        });

        setModalType(
          ModalType.CHECK_OUT_SUCCESS,
          `${kydEvent?.display_mode}#${kydEvent?.name}`
        );
      } else if (didFind === "failed") {
        analytics.t("cart: checkout failed", { mode: "3ds" });
        setModalType(ModalType.CHECK_OUT_FAILED);
      } else if (didFind === "duplicate") {
        logger.log("Ignore");
      } else {
        analytics.t("cart: checkout timed out", { mode: "3ds" });
        setModalType(ModalType.CHECK_OUT_TIMED_OUT);
      }

      queryParams.delete("checkout_session_id");
      queryParams.delete("mode");
      queryParams.delete("payment_intent");
      queryParams.delete("source_type");
      queryParams.delete("payment_intent_client_secret");

      const newUrl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?" +
        queryParams.toString();
      window.history.pushState({ path: newUrl }, "", newUrl);
    };

    if (queryParams.has("mode") && queryParams.get("mode") === "payment") {
      checkFor3ds();
    } else if (queryParams.has("m") && queryParams.get("m") === "1") {
      queryParams.delete("m");
      const newUrl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?" +
        queryParams.toString();
      window.history.pushState({ path: newUrl }, "", newUrl);
      setModalType(ModalType.SIGN_IN_PHONE_NUMBER, "Register Now");
    }
  }, []);

  useEffect(() => {
    const fetchKYDEvent = async (_event_id: string) => {
      updateLoadingState("code", true);
      try {
        const _ = await actions.USER.fetchEvent(_event_id);
        analytics.t("event: fetched event");
        analytics.t("pageview");

        if (_) {
          document.title = _.name;
        }
        setKydEvent(_);
      } catch (err: any) {
        //TODO: show error
        analytics.t("error: failed to load event", {
          message: err && err.message,
        });
      }
      updateLoadingState("code", false);
    };

    if (event_id) {
      fetchKYDEvent(event_id);
      PIXELS.pageview();
    }
  }, [event_id, queryParams, code]);

  const draftCartFromCart = useCallback(
    (cart: KYDCartV2) => {
      // Editing a cart
      const lineItemsToQuantity = cart.line_items.reduce(
        (ticket_types, lineItem) => {
          const ticketTypeId = lineItem.entity_type_id;

          if (ticket_types[lineItem.entity_type_id]) {
            return {
              ...ticket_types,
              [ticketTypeId]: ticket_types[ticketTypeId] + 1,
            };
          }

          return {
            ...ticket_types,
            [ticketTypeId]: 1,
          };
        },
        {}
      );

      return {
        ticket_types: lineItemsToQuantity,
        total: cart.line_items.length,
        id: cart.id,
      };
    },
    [draftCart, cart]
  );

  useEffect(() => {
    const initCart = async (_event_id: string, _attempts?: number) => {
      if (postLoginAction === "add_to_cart") {
        return;
      }

      clearDraftCart();

      if (currentUser) {
        updateLoadingState("cart", true);
        updateLoadingState("wallet", true);
        try {
          const serverCart = await fetchCart(_event_id);
          if (serverCart) {
            analytics.t("cart: fetched cart");
            setCart({ ...serverCart });

            if (
              serverCart.expires_seconds &&
              !queryParams.has("checkout_session_id")
            ) {
              setExpiresMs(Date.now() + serverCart.expires_seconds * 1000);
            } else {
              setExpiresMs(0);
            }
          } else {
            dialog({ text: "No cart found. Please contact support." });
          }
        } catch (err) {
          dialog({ text: err.message });
        }
        updateLoadingState("cart", false);

        try {
          await fetchWallet(_event_id);
        } catch (err) {
          //TODO: show error
          //@ts-ignore
          dialog({ text: err.message });
        }
        updateLoadingState("wallet", false);
      }
    };

    if (event_id) {
      initCart(event_id);
    }
  }, [
    event_id,
    postLoginAction,
    currentUser,
    queryParams,
    setCart,
    setDraftCart,
    fetchCart,
    fetchWallet,
    updateLoadingState,
    dialog,
  ]);

  const isUserLoggedIn = !!(currentUser && currentUser.getSignInUserSession());
  const onJoinWaitlist = useCallback(
    (ticketType: KYDTicketType) => {
      if (ticketTypeHasWaitlistBuyOrder(ticketType, wallet)) {
        setWaitlistAction(null);
        setWaitlistTicketTypes([]);

        return;
      }
      // Open popup to select number of waitlist tickets
      setWaitlistTicketTypes([ticketType]);
      setWaitlistAction("buy");
      waitlistDialog.open({
        title: "Get in line",
        text: "How many tickets do you need? Pro-tip: The fewer tickets you request, the higher your chances we’ll be able find you tickets as they become available! ",
      });
    },
    [setWaitlistTicketTypes, waitlistDialog]
  );

  const onLeaveWaitlist = useCallback(
    async (tt: KYDTicketType, wo: KYDWaitlistBuyOrder) => {
      setLoading({
        waitlist: true,
      });
      try {
        const response = await actions.USER.cancelWaitlistOrder({
          event_id: kydEvent?.id,
          waitlist_id: wo.id,
        });
        await fetchWallet(kydEvent.id);
        fullScreenModal.open({
          title: "✅ Removed from waitlist",
          text: "You are no longer on the waitlist for the event",
        });
      } catch (e) {
        logger.error("Waitlist cancel", e);
        fullScreenModal.open({
          title: "Something is wrong",
          text: e.message,
        });
      } finally {
        setLoading({ waitlist: false });
      }
    },
    [kydEvent]
  );

  const onSignInToJoinWaitlist = useCallback((ticketType: KYDTicketType) => {
    if (isUserLoggedIn) {
      return;
    }

    setPostLoginAction("waitlist");
    setWaitlistTicketTypes([ticketType]);
    setWaitlistAction("buy");
    setModalType(ModalType.SIGN_IN_PHONE_NUMBER);
  }, []);

  const onUpdateCart = useCallback(
    async (_draftCart: any, versionHash?: string) => {
      logger.log("onUpdateCart: ", { _draftCart, cart });
      setModalType(ModalType.SECURING_TICKETS, kydEvent?.display_mode);
      const tt_ids = Object.keys(_draftCart?.ticket_types);
      let addToCartPixelData: AddToCart = {
        currency: kydEvent?.currency,
        contents: tt_ids.map((ttid) => ({
          id: ttid,
          quantity: _draftCart.ticket_types[ttid],
        })),
        content_type: "product",
      };
      analytics.t("cart: added to cart");

      const addToCartEventId = getRandomString(32);
      PIXELS.addtocart(
        addToCartPixelData,
        addToCartEventId,
        kydEvent?.pixels && kydEvent.pixels.twitter
          ? kydEvent.pixels.twitter.event_add_to_cart_id
          : null
      );
      if (currentUser && currentUser.getSignInUserSession()) {
        try {
          // Generate the targetCart based on the draftCart
          const targetCart = {
            ..._draftCart,
            ticket_types: { ..._draftCart.ticket_types }
          };

          // Add the quantity from cart to targetCart
          if (cart && cart.line_items?.length > 0) {
            cart.line_items.forEach((line_item) => {
              logger.log("Adding 1 to ticket type to targetCart from cart: ", line_item.entity_type_id);
              if (targetCart.ticket_types[line_item.entity_type_id]) {
                targetCart.ticket_types[line_item.entity_type_id] += 1;
              } else {
                targetCart.ticket_types[line_item.entity_type_id] = 1;
              }
            });
          }

          // Filter out from targetCart ticket types with quantity 0 or less
          Object.keys(targetCart.ticket_types).forEach((ttid) => {
            if (targetCart.ticket_types[ttid] <= 0) {
              delete targetCart.ticket_types[ttid];
            }
          });
          // Re-Calculate the targetCart total
          targetCart.total = Object.keys(targetCart.ticket_types).reduce((acc, ttid) => acc + targetCart.ticket_types[ttid], 0);

          logger.log("onUpdateCart: ", { targetCart });

          // Checks if request contains hidden ticket types
          // If so, we need to pass the access code to the backend
          const ticketTypeIds = Object.keys(targetCart.ticket_types);
          const needsAccessCode = ticketTypeIds.some(ttid => {
            const ticketType = kydEvent.ticket_types.find(tt => tt.id === ttid);
            return ticketType?.hidden === true;
          });

          const cart_result = await actions.USER.updateCart(
            event_id!,
            targetCart,
            versionHash || cart?.update_token,
            needsAccessCode ? accessCode : null,
            addToCartEventId
          );
          if (cart_result.success === true) {
            // Fetch the new cart
            const newCart = await actions.USER.fetchCart(event_id!);
            setCart({ ...newCart });
            clearDraftCart();

            if (expiresMs === 0 && newCart.expires_seconds) {
              setExpiresMs(Date.now() + newCart.expires_seconds * 1000);
            } else if (!newCart.expires_at) {
              setExpiresMs(0);
            }

            setModalType(ModalType.INACTIVE);
            const searchParams = new URLSearchParams(window.location.search);
            navigate(`/e/${event_id}/cart?${searchParams.toString()}`);
          } else if (cart_result.success === false && cart_result.message) {
            setModalType(ModalType.INACTIVE);
            //@ts-ignore
            dialog({ text: cart_result.message });
          }
        } catch (err) {
          setModalType(ModalType.INACTIVE);
          setDraftCart(draftCartFromCart(cart));
          //@ts-ignore
          dialog({ text: err.message });
        } finally {
          const _kydEvent = await actions.USER.fetchEvent(event_id!);
          setKydEvent(_kydEvent);
        }
      } else {
        analytics.t("cart: needs auth");
        setDraftCart({ ..._draftCart, interupted: true });
        setPostLoginAction("add_to_cart");
        setModalType(ModalType.SIGN_IN_PHONE_NUMBER);
      }
    },
    [
      accessCode,
      event_id,
      setPostLoginAction,
      kydEvent,
      cart,
      setExpiresMs,
      currentUser,
    ]
  );

  const onRemindMe = useCallback(
    async (eventId: string) => {
      if (currentUser && currentUser.getSignInUserSession()) {
        setModalType(ModalType.CREATING_REMINDER);
        let remindMePixelData: any = {
          kyd_event_id: eventId,
        };
        analytics.t("event: reminder set");

        PIXELS.setReminder(remindMePixelData);

        try {
          await actions.USER.createEventReminder(eventId);
          setModalType(ModalType.INACTIVE);
          toast({
            title: "✅ Reminder Created",
            description: "We'll send you a text as soon as tickets go on sale",
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        } catch (err) {
          setModalType(ModalType.INACTIVE);
          dialog({ text: err.message });
        }
      } else {
        setDraftCart({ ...draftCart, reminder: true });
        analytics.t("event: remind me clicked");
        setModalType(ModalType.SIGN_IN_PHONE_NUMBER);
      }
    },
    [currentUser, event_id, kydEvent, setModalType, dialog]
  );

  const onClearCart = useCallback(async () => {
    analytics.t("cart: clear cart");
    setModalType(ModalType.CLEARING_CART);
    if (cart && cart.expiration_token) {
      try {
        setExpiresMs(0);

        await actions.USER.clearCart(event_id!, cart?.expiration_token);
        const newCart = await actions.USER.fetchCart(event_id!);
        setCart({ ...newCart });
        clearDraftCart();

        const _kydEvent = await actions.USER.fetchEvent(event_id!);
        setKydEvent(_kydEvent);

        setModalType(ModalType.INACTIVE);
      } catch (err) {
        setModalType(ModalType.INACTIVE);
        dialog({ text: err.message });
      }
    } else {
      throw Error("No expiration token on cart. Please contact support.");
    }
  }, [event_id, kydEvent, cart]);

  useEffect(() => {
    const finishCart = async () => {
      setModalType(ModalType.SECURING_TICKETS, kydEvent?.display_mode);
      const cart = await fetchCart(event_id);
      onUpdateCart(draftCart, cart?.update_token);
    };

    if (currentUser && currentUser.getSignInUserSession()) {
      if (postLoginAction === "add_to_cart") {
        finishCart();
        setPostLoginAction(null);
      } else if (draftCart && draftCart.reminder) {
        onRemindMe(event_id);
      } else if (postLoginAction === "waitlist") {
        setPostLoginAction(null);
        onJoinWaitlist(waitlistTicketTypes.at(0));
      }
    }
  }, [
    currentUser,
    postLoginAction,
    draftCart,
    cart,
    fetchCart,
    setPostLoginAction,
    onUpdateCart,
    onRemindMe,
    event_id,
  ]);

  const onCheckout = async (params: CheckoutParams) => {
    let { cardHolder, formData, type } = params;

    let form_payment_method_id = formData.payment_method_id;
    analytics.t("cart: checked out", { type });

    if (formData) {
      cardHolder = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        billing_address: {
          zip: formData.zip,
        },
        store_for_future_use: false,
      };
    }

    const total = cart?.total || 0;
    const cartId = cart!.id;
    const cartCurrency = cart!.currency;
    const cartSubtotal = total / 100;
    const cartQuantity = cart!.ticket_quantity;
    const cartContents = cart!.display_line_items.map((li) => ({
      id: li.entity_type_id,
      quantity: li.display_quantity,
    }));
    let intiateCheckoutData: InitiateCheckout = {
      currency: cartCurrency,
      value: cartSubtotal,
      num_items: cartQuantity,
      contents: cartContents,
    };
    PIXELS.identify({ email: cardHolder?.email });
    PIXELS.initiatecheckout({
      data: intiateCheckoutData,
      twitterEventId:
        kydEvent?.pixels && kydEvent.pixels.twitter
          ? kydEvent.pixels.twitter.event_initiate_checkout_id
          : null,
      kydEvent,
      eventId: kydEvent?.id,
      cartId,
    });

    setCheckoutLoading(true);
    setModalType(ModalType.CHECKING_OUT, kydEvent?.display_mode);

    try {
      let payment_method_id = null;
      if (type !== "free") {
        // Trigger form validation and wallet collection
        const { error: submitError } = await elementsRef.current.submit();
        if (submitError) {
          throw new Error(submitError.message);
        }

        if (!stripeRef || !stripeRef.current) {
          throw new Error(
            "Could not create checkout session. Your card was not charged. Please refresh the page and try again."
          );
        }

        if (form_payment_method_id && type === "card") {
          const cvc_element = elementsRef!.current.getElement("cardCvc");

          const { token, error: cvcError } =
            await stripeRef!.current.createToken("cvc_update", cvc_element);

          if (cvcError) {
            throw new Error(cvcError.message);
          }
          cardHolder.cvc_token = token.id;
          payment_method_id = form_payment_method_id;
        } else {
          const { paymentMethod, error: pmError } =
            await stripeRef!.current.createPaymentMethod({
              elements: elementsRef!.current,
            });

          if (pmError) {
            throw new Error(pmError.message);
          }

          payment_method_id = paymentMethod.id;
        }
      }

      const { checkout_session_id, payment_intent } =
        await actions.USER.createCheckout(
          event_id!,
          total,
          cardHolder,
          type,
          payment_method_id,
          radarRef.current
        );

      setCart(null);
      setExpiresMs(0);

      if (payment_intent && payment_intent.next_action) {
        const { error } = await stripeRef!.current.handleNextAction({
          clientSecret: payment_intent.client_secret,
        });

        if (error) {
          throw new Error(error?.message || "Could not handle next action");
        }
      }

      let purchaseData: Purchase = {
        currency: cart?.currency || "USD",
        value: (cart?.total || 0) / 100,
        num_items: cart.ticket_quantity,
        contents: cart?.display_line_items.map((li) => ({
          id: li.entity_type_id,
          quantity: li.display_quantity,
        })),
      };
      PIXELS.purchase(
        purchaseData,
        checkout_session_id,
        kydEvent?.pixels && kydEvent.pixels.twitter
          ? kydEvent.pixels.twitter.event_purchase_id
          : null,
        null,
        cart?.id
      );

      analytics.t("cart: checkout completed", {
        value: (cart?.total || 0) / 100,
        purchaseData,
      });

      const didFind = await pollWalletForNewTickets(checkout_session_id);

      if (didFind === "success") {
        analytics.t("cart: checkout success", {
          value: (cart?.total || 0) / 100,
          purchaseData,
        });

        setModalType(
          ModalType.CHECK_OUT_SUCCESS,
          `${kydEvent?.display_mode}#${kydEvent?.name}`
        );
        clearDraftCart();
      }
    } catch (err) {
      setModalType(ModalType.INACTIVE);
      logger.error(err);
      dialog({
        text:
          err.message ||
          "Could not create checkout session. Please contact support.",
      });
    } finally {
      const serverCart = await fetchCart(event_id!);
      setCart(serverCart);
      if (serverCart?.expires_seconds) {
        setExpiresMs(Date.now() + serverCart.expires_seconds * 1000);
      }
    }

    setCheckoutLoading(false);
  };

  const onCreateWaitlistBuyOrder = async (params: CheckoutParams) => {
    let { cardHolder, formData, type } = params;

    let form_payment_method_id = formData.payment_method_id;
    analytics.t("waitlist: checkout", { type });

    if (formData) {
      cardHolder = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        billing_address: {
          zip: formData.zip,
        },
        store_for_future_use: false,
      };
    }

    // const total = cart?.total || 0;
    // const cartCurrency = cart!.currency;
    // const cartSubtotal = total / 100;
    // const cartQuantity = cart!.ticket_quantity;
    // const cartContents = cart!.display_line_items.map((li) => ({
    //   id: li.entity_type_id,
    //   quantity: li.display_quantity,
    // }));
    // let intiateCheckoutData: InitiateCheckout = {
    //   currency: cartCurrency,
    //   value: cartSubtotal,
    //   num_items: cartQuantity,
    //   contents: cartContents,
    // };
    // PIXELS.identify({ email: cardHolder?.email });
    // PIXELS.initiatecheckout(
    //   intiateCheckoutData,
    //   null,
    //   kydEvent?.pixels && kydEvent.pixels.twitter
    //     ? kydEvent.pixels.twitter.event_initiate_checkout_id
    //     : null
    // );

    setCheckoutLoading(true);

    try {
      let payment_method_id = null;

      // Trigger form validation and wallet collection
      const { error: submitError } = await elementsRef.current.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      if (!stripeRef || !stripeRef.current) {
        throw new Error(
          "Could not create checkout session. Your card was not charged. Please refresh the page and try again."
        );
      }

      if (form_payment_method_id && type === "card") {
        const cvc_element = elementsRef!.current.getElement("cardCvc");

        const { token, error: cvcError } = await stripeRef!.current.createToken(
          "cvc_update",
          cvc_element
        );

        if (cvcError) {
          throw new Error(cvcError.message);
        }
        cardHolder.cvc_token = token.id;
        payment_method_id = form_payment_method_id;
      } else {
        const { paymentMethod, error: pmError } =
          await stripeRef!.current.createPaymentMethod({
            elements: elementsRef!.current,
          });

        if (pmError) {
          throw new Error(pmError.message);
        }
        payment_method_id = paymentMethod.id;
      }

      const waitlistTicketType = waitlistCart.line_items.at(0);

      if (!waitlistTicketType) {
        throw new Error("Ticket type not in waitlist");
      }

      const { id, setup_intent } = await actions.USER.createBuyOrder({
        ticket_type_id: waitlistTicketType.entity_type_id,
        quantity: waitlistCart.ticket_quantity,
        card_holder: cardHolder,
        event_id: kydEvent.id,
        type: "card",
        expected_total: waitlistCart.total,
        payment_method_id: payment_method_id,
        radar_session: radarRef.current,
        auto_cancel_settings: waitlistCancellationSettings.enabled ? waitlistCancellationSettings : null,
      });

      if (setup_intent?.next_action?.redirect_to_url?.url) {
        setWaitlistPaymentSetupUrl(
          setup_intent?.next_action?.redirect_to_url?.url
        );

        waitlistPaymentSetupDialog.open({
          title: "Payment method confirmation",
        });
      } else if (id) {
        onPaymentMethodSetupSuccess();
      }
    } catch (err) {
      setModalType(ModalType.INACTIVE);
      logger.error(err);
      fullScreenModal.open({
        title: "❌ Something went wrong",
        text:
          err.message ||
          "Could not reserve tickets for waitlist. Please contact support.",
      });
      setCheckoutLoading(false);
    }
  };

  const onPaymentMethodSetupSuccess = useCallback(async () => {
    const wallet = await fetchWallet(kydEvent.id);
    const hasPending = wallet.buy_waitlist_orders.some(
      (wo) => wo.status === "PENDING_PAYMENT_METHOD"
    );

    const onSuccess = () => {
      fullScreenModal.open({
        title: "✅ You've been added to the waitlist!",
        text: "👋 You will be notified if tickets become available and charged on your card.",
      });
      setWaitlistCart(null);
      setWaitlistTicketTypes([]);
      setWaitlistAction(null);
      setCheckoutLoading(false);
      fetchWallet(kydEvent.id);
      navigate(`/e/${kydEvent.id}`);
    };

    if (!hasPending) {
      onSuccess();
      return;
    }

    if (hasPending) {
      const isSuccess = await startWalletPolling();
      if (isSuccess) {
        onSuccess();
      } else {
        fullScreenModal.open({
          title: "❌ Something went wrong",
          text: "We were not able to confirm the payment method",
        });
      }

      setCheckoutLoading(false);
    }
  }, [
    toast,
    setWaitlistCart,
    setWaitlistTicketTypes,
    fetchWallet,
    navigate,
    kydEvent,
  ]);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.data === "3DS-authentication-complete") {
        onPaymentMethodSetupSuccess();
      }
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [
    kydEvent,
    setWaitlistCart,
    setWaitlistTicketTypes,
    fetchWallet,
    navigate,
    toast,
    onPaymentMethodSetupSuccess,
  ]);

  const [promocodeErrors, updatePromocodeErrors, cleanError] = useErrors();
  const onApplyPromoCode = async (promoCode: string) => {
    analytics.t("cart: apply promo code", { promoCode });

    setCheckoutLoading(true);
    cleanError("promocode");
    return actions.USER.applyPromoCode(event_id!, promoCode)
      .then(async () => {
        const serverCart = await fetchCart(event_id!);
        setCart(serverCart);
      })
      .catch((e) => {
        logger.error(e);
        updatePromocodeErrors({
          promocode: {
            message: e.message || "Invalid promo code",
          },
        });

        throw e;
      })
      .finally(() => {
        setCheckoutLoading(false);
      });
  };

  const onRemovePromocode = async (promoCode: string) => {
    analytics.t("cart: remove promo code", { promoCode });

    setCheckoutLoading(true);
    cleanError("promocode");
    return actions.USER.removePromoCode(event_id!, promoCode)
      .then(async () => {
        const serverCart = await fetchCart(event_id!);
        setCart(serverCart);
      })
      .catch((e) => {
        logger.error(e);
        updatePromocodeErrors({
          promocode: {
            message: e.message || "Could not remove promocode",
          },
        });

        throw e;
      })
      .finally(() => {
        setCheckoutLoading(false);
      });
  };

  const onUpdateTicketTypeQuantity = useCallback(
    (
      ticket_type: {
        id: KYDTicketType["id"];
        limit: KYDTicketType["limit"];
        remaining?: KYDTicketType["remaining"];
        increment?: KYDTicketType["increment"];
      },
      action: string,
      value = 0
    ) => {
      analytics.t("cart: update quantity", { action, ticket_type });

      const increment = ticket_type.increment || 1;

      const hasTicketTypesInDraftCart = draftCart?.ticket_types
        ? Object.keys(draftCart?.ticket_types).length > 0
        : false;

      if (!draftCart.ticket_types) {
        draftCart.ticket_types = {};
      }

      const current_quantity = draftCart.ticket_types[ticket_type.id] || 0;
      const current_cart_quantity = cart?.line_items
        ? cart.line_items.filter(li => li.entity_type_id === ticket_type.id).length
        : 0;

      // Can add when total of cart + draft cart + increment is less than limit
      // and if there's remaining, then the draft cart + increment has to be less than remaining (updated on cart update)
      const canAdd =
        current_cart_quantity + current_quantity + increment <= ticket_type.limit &&
        (ticket_type.remaining === undefined || current_quantity + increment <= ticket_type.remaining);
      const canSubtract =
        current_cart_quantity + current_quantity - increment >= 0;

      if (action === "add" && canAdd) {
        draftCart.ticket_types[ticket_type.id] = current_quantity + increment;
      } else if (action === "sub" && canSubtract) {
        // If ticket type is not in draft cart but in cart, subtract the increment (negative quantity)
        // so that the combination between draft cart and cart is correct
        // this is mainly for actions from the cart view
        if(draftCart.ticket_types[ticket_type.id]) {
          draftCart.ticket_types[ticket_type.id] = current_quantity - increment;
        } else {
          draftCart.ticket_types[ticket_type.id] = 0 - increment;
        }
      } else if (action === "set") {
        draftCart.ticket_types[ticket_type.id] = value;
      }

      if (action === "delete") {
        // If ticket type is in draft cart, delete it, otherwise set it to negative from cart quantity
        // so that the combination between draft cart and cart is correct
        // this is mainly for actions from the cart view
        if(draftCart.ticket_types[ticket_type.id]) {
          delete draftCart.ticket_types[ticket_type.id];
        }
        else {
          draftCart.ticket_types[ticket_type.id] = 0 - current_cart_quantity;
        }
      }

      let total = 0;
      const ticket_types = Object.keys(draftCart.ticket_types);
      ticket_types.forEach((key) => {
        total += draftCart.ticket_types[key];
      });

      draftCart.total = total;

      setDraftCart({ ...draftCart });

      logger.log("updateTicketTypeQuantity: ", { id: ticket_type.id, draftCartQuantity: current_quantity + increment, cartQuantity: current_cart_quantity });
      return draftCart;
    },
    [draftCart, kydEvent, cart]
  );

  const onCartExpire = useCallback(async () => {
    try {
      analytics.t("cart: expired");
      setCart(null);
      setExpiresMs(0);
      const cart = await fetchCart(event_id!);
      if (cart && (cart.status === "EXPIRED" || cart.status === "OPEN")) {
        setModalType(ModalType.EXPIRED_CART);
      }
    } catch (err) {
      setModalType(ModalType.EXPIRED_CART);
    } finally {
      const serverCart = await fetchCart(event_id!);
      clearDraftCart();
      setCart({
        ...serverCart,
        ticket_quantity: 0,
        line_items: [],
        display_line_items: [],
      });
      const _kydEvent = await actions.USER.fetchEvent(event_id!);
      setKydEvent(_kydEvent);
      if (serverCart?.expires_seconds) {
        setExpiresMs(Date.now() + serverCart.expires_seconds * 1000);
      }
    }
  }, [event_id, kydEvent, fetchCart]);

  const goToWaitlistCheckout = async ({
    ticketTypeId,
    quantity,
    autoCancelSettings,
  }: {
    ticketTypeId: string;
    quantity: number;
    autoCancelSettings?: {
      enabled: boolean;
      daysBeforeEvent: number;
    };
  }) => {
    setLoading({
      waitlist: true,
    });

    if (autoCancelSettings) {
      setWaitlistCancellationSettings(autoCancelSettings);
    }

    const response = await actions.USER.mockWaitlistCart(kydEvent.id, {
      id: ticketTypeId,
      quantity,
    });

    logger.log("Cancellation settings:", autoCancelSettings);

    if (!response.statusCode) {
      setWaitlistCart(response);
    }

    setLoading({
      waitlist: false,
    });
    navigate(`/e/${event_id}/waitlist`);
  };

  // A map of ticket types with tickets
  const ticketTypesOfOwnedTickets = useMemo(() => {
    if (!wallet || !kydEvent) return [];

    return groupBy(wallet.tickets, (ticket) => ticket.ticket_type_id);
  }, [wallet, kydEvent]);

  const onCreateWaitlistSellOrder = useCallback(
    async ({
      ticketTypeId,
      ticketIds,
    }: {
      ticketTypeId: string;
      ticketIds: string[];
    }) => {
      // TODO: support multiple ticket types
      setLoading({
        waitlist: true,
      });
      try {
        await actions.USER.createSellOrder({
          event_id: kydEvent?.id,
          ticket_ids: ticketIds,
          ticket_type_id: ticketTypeId,
        });

        fullScreenModal.open({
          title:
            "✅ Confirmed! We'll notify you if we find someone to buy your tickets.",
          actions: [
            {
              text: "My account",
              type: "confirm",
              onClick: () => {
                navigate(`/e/${kydEvent.id}/wallet`);
              },
            },
          ],
        });

        await fetchWallet(kydEvent.id);
      } catch (e) {
        fullScreenModal.open({
          title: "❌ An error occurred",
          text: e.message,
        });
      } finally {
        setLoading({
          waitlist: false,
        });
      }
    },
    [kydEvent, ticketTypesOfOwnedTickets]
  );

  const onListTickets = () => {
    setWaitlistTicketTypes(
      Object.keys(ticketTypesOfOwnedTickets).map((ownedTicketTypeId) =>
        kydEvent.ticket_types.find(
          (tt) => tt.id === ownedTicketTypeId && tt.waitlist_status === "OPEN"
        )
      )
    );
    setWaitlistAction("sell");
    waitlistDialog.open({
      title: "Can't go anymore?",
      text: "You can add your tickets to the waitlist and get a full refund if someone buys it.",
    });
  };
  const descriptionWithLineBreaks = useMemo(() => {
    if (kydEvent?.description) {
      return markdownToLineBreaks(kydEvent.description);
    }
  }, [kydEvent?.description]);

  return (
    <Div100vh
      style={{
        background:
          "linear-gradient(322.9deg, #8B8B8B -32.49%, #000000 30.1%, #332E38 97.31%)",
        backgroundSize: "cover",
      }}
    >
      <FullScreenModal eventId={kydEvent?.id} />
      <JoinWaitlistModal
        action={waitlistAction}
        ticketTypes={waitlistTicketTypes}
        wallet={wallet}
        onCreateSellOrder={onCreateWaitlistSellOrder}
        onCreateBuyOrder={goToWaitlistCheckout}
        onCancel={() => {
          setWaitlistCart(null);
          setWaitlistTicketTypes([]);
          setWaitlistAction(null);
          setWaitlistCancellationSettings({
            enabled: false,
            daysBeforeEvent: 1,
          });
        }}
        isLoading={_loading.waitlist}
        kydEvent={kydEvent}
      />
      <WaitlistPaymentMethodConfirmationModal
        onCancel={() => {}}
        onConfirm={() => {}}
        url={waitlistPaymentSetupUrl}
      />

      <VStack overflowY={"scroll"} h="100%" justifyContent={"space-between"}>
        <Box zIndex={1000} w="100%" p={0}>
          {kydEvent && kydEvent.status === "PREVIEW" ? (
            <VStack
              justify={"center"}
              zIndex={1001}
              h="75"
              bg="blue.500"
              w="100%"
            >
              <Text fontWeight={"bold"} color="white">
                👀 YOU ARE PREVIEWING THIS EVENT
              </Text>
            </VStack>
          ) : null}
          <VStack zIndex={1001} w="100%">
            <HStack
              px={5}
              justify={"space-between"}
              w="100%"
              h={{ base: "100px", md: "150px" }}
            >
              <Image
                p={0}
                cursor={"pointer"}
                onClick={() => navigate(`/bio`)}
                src={kydEvent?.organization?.logo_url}
                objectPosition={"left"}
                objectFit={"contain"}
                maxW={{ base: "100px", md: "150px" }}
              />
              <HStack
                justify={"flex-end"}
                spacing={6}
                wrap="wrap"
                color="white"
              >
                {currentUser && (
                  <Text
                    cursor={"pointer"}
                    onClick={() => {
                      analytics.t("nav: log out");
                      setCart(null);
                      setWallet(null);
                      setExpiresMs(0);
                      Auth.signOut();
                      analytics.reset();
                    }}
                  >
                    Log Out
                  </Text>
                )}
                {!currentUser && (
                  <Text
                    cursor={"pointer"}
                    onClick={() => {
                      analytics.t("nav: sign in");
                      presentAuthModal();
                    }}
                  >
                    Sign-In
                  </Text>
                )}
                {kydEvent ? (
                  <Text
                    cursor={"pointer"}
                    onClick={() => {
                      analytics.t("nav: my tickets");
                      const searchParams = new URLSearchParams(
                        window.location.search
                      );

                      navigate(
                        `/e/${event_id}/wallet?${searchParams.toString()}`
                      );
                    }}
                  >
                    My{" "}
                    {kydEvent?.display_mode === "normal" ? "Tickets" : "RSVPs"}
                    {wallet && wallet.tickets.length > 0
                      ? ` (${wallet.tickets.length})`
                      : ""}
                  </Text>
                ) : null}

                <HStack
                  py={[4, null, 0]}
                  cursor={"pointer"}
                  onClick={() => {
                    analytics.t("nav: cart");
                    const searchParams = new URLSearchParams(
                      window.location.search
                    );
                    navigate(`/e/${event_id}/cart?${searchParams.toString()}`);
                  }}
                >
                  <Icon color="white" fontSize={"2xl"} as={FaShoppingCart} />

                  {cart &&
                    cart.ticket_quantity > 0 &&
                    !queryParams.has("checkout_session_id") && (
                      <Text
                        px="2"
                        fontSize={"md"}
                        fontWeight="bold"
                        rounded={"md"}
                        bg="red.500"
                        color="white"
                      >
                        {(cart as KYDCartV2).ticket_quantity}
                      </Text>
                    )}
                  {expiresMs > 0 && !queryParams.has("checkout_session_id") && (
                    <HStack
                      fontSize={"lg"}
                      fontWeight={"bold"}
                      color="white"
                      spacing={0}
                    >
                      <Text minW={"60px"}>
                        <KYDCountdown
                          ms={expiresMs}
                          didComplete={onCartExpire}
                        />
                      </Text>
                    </HStack>
                  )}
                </HStack>
              </HStack>
            </HStack>
          </VStack>
        </Box>
        {kydEvent && (
          <VStack
            pt={{ base: "0px", md: "50px" }}
            pb={[0, null, 10]}
            spacing={{ base: 0, md: 0 }}
            w="100%"
            maxW={["100%", null, "4xl"]}
            flexGrow={4}
          >
            <Box
              w="100%"
              bg={{ base: "transparent", md: "white" }}
              rounded={{ base: "none", md: "lg" }}
              borderColor="gray.200"
              shadow={"lg"}
              borderWidth={{ base: "0px", md: "1px" }}
              p={[3, null, 5]}
              mb={{ base: 0, md: 4 }}
            >
              <Stack
                flexDir={["column", null, "row"]}
                spacing={{ base: 2, md: 0 }}
                alignItems={{ base: "center", md: "flex-start" }}
                w="100%"
              >
                {/* event image */}
                <EventImage
                  posterImage={kydEvent?.image}
                  story={kydEvent?.story}
                  mr={[0, null, 4]}
                />
                <Stack
                  color={{ base: "white", md: "black" }}
                  textAlign={"left"}
                  flexGrow={1}
                >
                  <Stack spacing={{ base: 2, md: 4 }}>
                    {/* name of event */}
                    <Stack spacing={0}>
                      <Text
                        onClick={() => {
                          const searchParams = new URLSearchParams(
                            window.location.search
                          );
                          navigate(`/e/${event_id}?${searchParams.toString()}`);
                        }}
                        cursor="pointer"
                        fontSize={"2xl"}
                        fontWeight={"bold"}
                      >
                        {kydEvent.name}
                      </Text>
                      {kydEvent.subtitle ? (
                        <Text w="full">{kydEvent.subtitle}</Text>
                      ) : null}
                    </Stack>

                    {kydEvent?.display_options?.attendees_hosted ? (
                      <VStack w="full" alignItems={"flex-start"}>
                        <Box
                          bg="red.200"
                          rounded={"md"}
                          px={2}
                          py={1}
                          fontSize={"xs"}
                          color={["gray.800", "white"]}
                        >
                          <InlineText fontWeight={"bold"} mr={1}>
                            {kydEvent?.display_options.attendees_hosted}
                          </InlineText>
                          attendees hosted 📈
                        </Box>
                      </VStack>
                    ) : null}
                    {/* start date */}
                    <HStack align={"top"}>
                      <Icon mt={1} as={FaCalendar} />
                      <Text
                        display={{ base: "none", md: "block" }}
                        fontWeight={"medium"}
                      >
                        {`${kydEvent.display_start_at}`}
                      </Text>
                      <HStack>
                        <Text
                          display={{ base: "block", md: "none" }}
                          fontWeight={"medium"}
                        >
                          {`${kydEvent.display_start_at}`}
                        </Text>
                      </HStack>
                    </HStack>
                    {/* location */}
                    <HStack align={"center"}>
                      <Icon mt={1} as={FaMapPin} />
                      <Stack spacing={0}>
                        <Text fontWeight={"bold"}>
                          <a
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: linkColor,
                              textDecoration: linkDecoration,
                              fontStyle: "bold",
                            }}
                            href={kydEvent.venues[0]?.link}
                          >
                            {kydEvent.venues[0]?.name}
                          </a>
                        </Text>
                        <Stack spacing={0}>
                          <Text fontSize={"sm"} fontWeight={"medium"}>
                            {kydEvent.venues[0]?.address}
                          </Text>
                        </Stack>
                      </Stack>
                    </HStack>
                    {/*kydEvent.display_doors_at ? (
                      <HStack>
                        <Icon
                          color={{ base: "white", md: "gray.500" }}
                          as={FaDoorOpen}
                        />
                        <Stack spacing={0}>
                          <Text
                            color={{ base: "white", md: "gray.500" }}
                            fontSize={"sm"}
                            fontWeight={"medium"}
                          >
                            {`Doors open at ${kydEvent.display_doors_at}`}
                          </Text>
                        </Stack>
                      </HStack>
                    ) : null*/}
                  </Stack>

                  <Box display={{ base: "none", md: "block" }}>
                    <Stack
                      spacing={1}
                      pos="relative"
                      onClick={() => setExpanded(!isExpanded)}
                      {...getCollapseProps()}
                    >
                      {descriptionIsExpandable && !isExpanded ? (
                        <Box
                          pointerEvents={"none"}
                          h="50px"
                          pos="absolute"
                          right={0}
                          left={0}
                          bottom={0}
                          background={
                            !isExpanded
                              ? `linear-gradient(to bottom, transparent, ${
                                  isMobile ? "black" : "white"
                                })`
                              : null
                          }
                        />
                      ) : null}

                      {kydEvent.description_rich ? (
                        <BodyDocRenderer
                          content={kydEvent.description_rich}
                          isMobile={isMobile}
                        />
                      ) : descriptionWithLineBreaks ? (
                        <Markdown
                          //@ts-ignore
                          className="mdx-editor"
                          components={{
                            a: (props) => (
                              <Link
                                href={props.href}
                                target={"_blank"}
                                textDecoration={"underline"}
                                color={isMobile ? "blue.200" : "blue.800"}
                              >
                                {props.children}
                              </Link>
                            ),
                            h1: (props) => <Heading {...props} fontSize={24} />,
                            h2: (props) => <Heading {...props} fontSize={22} />,
                            h3: (props) => <Heading {...props} fontSize={20} />,
                            h4: (props) => <Heading {...props} fontSize={18} />,
                            h5: (props) => <Heading {...props} fontSize={16} />,
                            p: Text,
                          }}
                        >
                          {descriptionWithLineBreaks}
                        </Markdown>
                      ) : null}
                    </Stack>
                    {kydEvent.description &&
                    kydEvent.description.length > 50 ? (
                      <Text
                        onClick={() => setExpanded(!isExpanded)}
                        cursor="pointer"
                        fontSize="sm"
                        color="gray.500"
                        textAlign="left"
                        w="full"
                        fontWeight="bold"
                      >
                        {isExpanded ? "See less" : "See event details"}
                      </Text>
                    ) : null}
                    {/* {kydEvent.performers.length > 0 ? (
                      <Divider mt={4} w="full" />
                    ) : null} */}
                    {kydEvent.performers.length > 0 ? (
                      <VStack
                        mt={3}
                        display={{ base: "none", md: "block" }}
                        gap={0}
                        w="full"
                        alignItems="flex-start"
                      >
                        <Lineup
                          performers={kydEvent.performers}
                          onToggleSetTimes={() =>
                            setIsSetTimesExpanded((prev) => !prev)
                          }
                          isSetTimesExpanded={isSetTimesExpanded}
                        />
                      </VStack>
                    ) : null}
                  </Box>
                </Stack>
              </Stack>
            </Box>
            <Box
              w="100%"
              rounded={{ base: "none", md: "lg" }}
              borderColor="gray.200"
              shadow={"lg"}
            >
              <Stack spacing={0}>
                <Routes>
                  <Route
                    path="cart"
                    element={
                      <Cart
                        wallet={wallet}
                        stripeObject={stripeObject}
                        stripeRef={stripeRef}
                        elementsRef={elementsRef}
                        radarRef={radarRef}
                        kydEvent={kydEvent}
                        currentUser={currentUser}
                        cart={cart!}
                        onCheckout={onCheckout}
                        onSignIn={presentAuthModal}
                        onClearCart={onClearCart}
                        checkoutLoading={checkoutLoading}
                        cleanPromocodeErrors={() => cleanError("promocode")}
                        onUpdateTicketTypeQuantity={async (...params) => {
                          const draftCart = onUpdateTicketTypeQuantity(
                            ...params
                          );
                          await onUpdateCart(draftCart);
                        }}
                        promocodeErrors={promocodeErrors}
                        onApplyPromoCode={onApplyPromoCode}
                        onRemovePromocode={onRemovePromocode}
                        modalType={modalType}
                      />
                    }
                  />
                  <Route
                    path="wallet"
                    element={
                      <KYDEventWallet
                        kydEvent={kydEvent}
                        onListTickets={onListTickets}
                        authState={authState}
                        loading={_loading}
                        setLoading={setLoading}
                        wallet={wallet}
                        setModalType={setModalType}
                        onSignIn={presentAuthModal}
                        fetchWallet={fetchWallet}
                        onLeaveWaitlist={onLeaveWaitlist}
                      />
                    }
                  />
                  <Route
                    path="success"
                    element={<KYDSuccess kydEvent={kydEvent} />}
                  />
                  <Route
                    path="waitlist"
                    element={
                      <WaitlistCheckout
                        stripeObject={stripeObject}
                        kydEvent={kydEvent}
                        onCreateWaitlistBuyOrder={onCreateWaitlistBuyOrder}
                        isLoading={checkoutLoading}
                        cart={waitlistCart}
                        stripeRef={stripeRef}
                        elementsRef={elementsRef}
                        radarRef={radarRef}
                      />
                    }
                  />
                </Routes>
              </Stack>
            </Box>
            <Routes>
              <Route
                path="/"
                element={
                  <KYDEventDetail
                    onGoToTicketCode={(code) => {
                      setSearchParams({ code });
                      setAccessCode(code);
                    }}
                    code={code}
                    wallet={wallet}
                    kydEvent={kydEvent}
                    cart={cart}
                    loading={_loading}
                    draftCart={draftCart}
                    onJoinWaitlist={
                      isUserLoggedIn ? onJoinWaitlist : onSignInToJoinWaitlist
                    }
                    onAddToCart={onUpdateCart}
                    onRemindMe={onRemindMe}
                    onUpdateTicketTypeQuantity={onUpdateTicketTypeQuantity}
                    modalType={modalType}
                  />
                }
              />
            </Routes>
            <EventFooter
              showIntercom={showIntercom}
              faq_link={kydEvent?.faq_link}
              logoFooterDark={logoFooterDark}
            />
          </VStack>
        )}

        <VStack
          display={{ base: "flex", md: "none" }}
          py={4}
          bg="transparent"
          w="100%"
          color="white"
        >
          <VStack pb={2} spacing={1}>
            <a
              href="https://kydlabs.com?ref=event"
              target="_blank"
              rel="noreferrer"
            >
              <Image maxW={"75px"} src={logoFooter} />
            </a>
            <HStack>
              <Text fontSize={"xs"} textDecor={"underline"}>
                <a
                  href="https://www.iubenda.com/terms-and-conditions/22517592"
                  target={"_blank"}
                  rel="noreferrer"
                >
                  Terms Of Service
                </a>
              </Text>
              <Text fontSize={"xs"} textDecor={"underline"}>
                <a
                  href="https://www.iubenda.com/privacy-policy/22517592"
                  target={"_blank"}
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              </Text>
              {kydEvent && kydEvent.faq_link && (
                <Text fontSize={"xs"} textDecor={"underline"}>
                  <a
                    href={kydEvent.faq_link}
                    target={"_blank"}
                    rel="noreferrer"
                  >
                    FAQ
                  </a>
                </Text>
              )}
              <Text
                onClick={() => showIntercom()}
                fontSize={"xs"}
                textDecor={"underline"}
                cursor="pointer"
              >
                Help
              </Text>
            </HStack>

            <HStack>
              <Text color="gray.200" fontSize={"xs"}>
                © {new Date().getFullYear()} KYD Labs Inc.
              </Text>
              <Text color="gray.400" fontSize={"xs"}>
                v{process.env.REACT_APP_VERSION}
              </Text>
            </HStack>
          </VStack>
        </VStack>
      </VStack>
    </Div100vh>
  );
}

export default withWaitlistModal(
  withWaitlistPaymentMethodConfirmationModal(withFullScreenModal(KYDEvent))
);
